import './styles.scss';

import { useEffect, useMemo } from 'react';
import useSession, { SessionUser } from '@/state/session';

import AccidentHistoryForm from '@/features/AccidentHistory/components/AccidentHistoryForm';
import { AccidentHistoryFormDefinitions } from '../AccidentHistoryForm/types';
import { AccidentIssueWithCache } from '@/models/gen/graphql';
import Popup from '@/common/Popup';
import { PopupTypes } from '@/common/Popup/types';
import { createComponentState } from '@/state';
import { getClasses } from '@/utils';
import useConfirmation from '@/hooks/useConfirmation';
import { useNavigate } from 'react-router-dom';

export type AccidentHistoryDrawerState = {
  show: boolean;
  data: Partial<AccidentIssueWithCache>;
};
export const initAccidentHistoryState: AccidentHistoryDrawerState = {
  show: false,
  data: {},
};
export const useAccidentHistoryDrawer = createComponentState<AccidentHistoryDrawerState>(initAccidentHistoryState);
export type AccidentHistoryDrawerProps = { locale?: Record<string, string> } & PopupTypes.Props;

const AccidentHistoryDrawer = ({ locale: customLocale, ...drawerProps }: AccidentHistoryDrawerProps): JSX.Element => {
  const [state, setState] = useAccidentHistoryDrawer(({ state, setState }) => [state, setState]);
  const { show, data } = state;
  const user = useSession(({ state }) => state.user || ({} as SessionUser));
  const navigate = useNavigate();
  const confirmHide = useConfirmation({
    Body: {
      message: (
        <div className="IssueConfirmation">
          <h5>Are you sure?</h5>
          <span>Any unsaved changes will be lost.</span>
        </div>
      ),
    },
  });

  const onHide = async (redirect: boolean = false): Promise<void> => {
    try {
      if (redirect) {
        AccidentHistoryFormDefinitions.clearForm();
        setState({ ...state, show: false });
      } else {
        const issueValue = AccidentHistoryFormDefinitions.loadForm();
        const issuesHasValue = Object.keys(issueValue).length > 0;
        if (issuesHasValue) await confirmHide();
        setState({ ...state, show: false });
      }
    } catch (err) {
      console.error('Error hiding drawer:', err);
    }
  };
  const onExited = (): void => setState(initAccidentHistoryState);

  const onSubmit = (data): void => {
    debug.log('accident-history,accident-history.drawer', 'Submit Accident', data);
    AccidentHistoryFormDefinitions.clearForm();
    onHide();
  };

  const gotoHistory = (): void => {
    onHide(true);
    navigate('/accident-history');
  };

  const actions = useMemo(() => {
    const result = [];
    if (!location.pathname.startsWith('/accident-history'))
      result.push({
        key: 'history',
        label: 'See History',
        onClick: gotoHistory,
      });
    return result.length > 0 ? result : undefined;
  }, [location.pathname, gotoHistory]);

  const value = useMemo(() => {
    if (!show) return undefined;
    const isDispatcher = user?.role?.name === 'dispatch';
    const dispatcherId = isDispatcher && !data?.dispatcherId ? user?.id : data?.dispatcherId;
    const result = { ...data, dispatcherId };
    debug.log('accident-history,accident-history.drawer', 'Default Value', result);
    return result;
  }, [show, data, user]);
  useEffect((): void => {
    if (show) return;
    AccidentHistoryFormDefinitions.clearForm();
  }, [show]);

  return (
    <Popup
      {...drawerProps}
      className={getClasses('AccidentHistoryDrawer', drawerProps.className)}
      show={show}
      onHide={onHide}
      onExited={onExited}
      actions={actions}
    >
      <AccidentHistoryForm onSubmit={onSubmit} value={value} key={data?.id} />
    </Popup>
  );
};

export default AccidentHistoryDrawer;
